import { alpha, createTheme } from '@mui/material/styles';
import { accordionClasses } from '@mui/material/Accordion';
import { alertClasses } from '@mui/material/Alert';
import { badgeClasses } from '@mui/material/Badge';
import { buttonClasses } from '@mui/material/Button';
import { chipClasses } from '@mui/material/Chip';
import { circularProgressClasses } from '@mui/material/CircularProgress';
import { inputBaseClasses } from '@mui/material/InputBase';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { componentTokens, darkPalette, lightPalette, ratingActiveFill } from './palette';
import {
  buttonSizeTypography,
  componentTypography,
  fontWeights,
  typography,
} from './typography';

const { light, dark } = componentTokens;

/**
 * The Figma library draws ListSubheader in two sizes — large on subtitle1,
 * medium on subtitle2. Material UI ships one size and no prop to pick between
 * them, so the prop is declared here. Without it a subheader could only reach
 * the large size through `sx`, which is the thing the theme exists to avoid.
 */
/**
 * The Figma library draws five button sizes; Material UI ships three. `xlarge`
 * and `xxlarge` are declared here so `size` accepts them, and styled in the
 * `variants` arrays below. They are SurePass additions — nothing in stock MUI
 * knows about them.
 */
declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xlarge: true;
    xxlarge: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    xlarge: true;
    xxlarge: true;
  }
}

/**
 * A count that sits beside a heading rather than on a host's corner.
 *
 * Material UI's `Badge` is an overlay primitive: the badge slot is
 * `position: absolute` and an anchor transform pins it to a child's corner.
 * With nothing to pin to there is no sanctioned way to put the same pill in
 * the flow of a row, so screens were hand-writing
 * `sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}` —
 * a restyle of a component into something it is not, which this system forbids.
 *
 * `variant="standalone"` is that deviation, declared once. MUI keeps the
 * badge's geometry in its base style and only overrides it for `dot`, so an
 * unknown variant inherits the standard pill and changes nothing but where it
 * sits.
 */
declare module '@mui/material/Badge' {
  interface BadgePropsVariantOverrides {
    standalone: true;
  }
}

/**
 * `variant="status"` — the tinted status chip, Figma nodes 11898:5392 (job
 * status) and 11899:42928 (invite status).
 *
 * It is a third look, and neither of MUI's two is it. `filled` paints the
 * palette colour solid and puts white on it; `outlined` is transparent with a
 * solid rule. The frames draw a **wash**: the colour at 14%, a 52% border, and
 * `text.primary` ink — so a row of them reads as a set of states rather than as
 * three loud buttons, and the label stays the same navy as the text beside it.
 *
 * Declared once here rather than retyped as `sx` per screen. A chip carrying a
 * hand-written `backgroundColor: alpha(green, 0.14)` is a chip that has stopped
 * following the theme, which is the rule this variant exists to keep.
 */
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    status: true;
  }
}

declare module '@mui/material/ListSubheader' {
  interface ListSubheaderOwnProps {
    size?: 'large' | 'medium';
  }
}

/**
 * The same wash on an Alert, for a banner that states a record's standing
 * rather than reporting the outcome of an action.
 *
 * `standard` and `filled` are loud on purpose — something just happened, or
 * just failed. A status banner is the opposite: it is true for as long as the
 * record is in that state, so it sits at the same weight as the status chip
 * beside it and lets the page's own text carry the reading.
 *
 * `default` is the fourth tone and the reason this is a `color` rather than a
 * `severity`. A standing notice is often neutral — "waiting on the agency" is
 * not good, bad or warning — and there is no grey severity. It borrows `info`
 * for the role and the icon slot and paints itself from `text.secondary`.
 */
declare module '@mui/material/Alert' {
  interface AlertPropsVariantOverrides {
    status: true;
  }
  interface AlertPropsColorOverrides {
    default: true;
  }
}

/** The three states a status chip carries. Green, yellow, red — nothing else. */
const STATUS_CHIP_COLORS = ['success', 'warning', 'error'] as const;

/** The status Alert adds a neutral fourth, which no `severity` provides. */
const STATUS_ALERT_COLORS = [...STATUS_CHIP_COLORS, 'default'] as const;

export const surepassTheme = createTheme({
  cssVariables: { colorSchemeSelector: 'class' },
  defaultColorScheme: 'light',
  colorSchemes: {
    light: { palette: lightPalette },
    dark: { palette: darkPalette },
  },
  typography,
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        '*::-webkit-scrollbar': { width: 12, height: 12 },
        '*::-webkit-scrollbar-track': {
          backgroundColor: light.scrollbarBg,
          ...theme.applyStyles('dark', { backgroundColor: dark.scrollbarBg }),
        },
        '*::-webkit-scrollbar-thumb': {
          borderRadius: 6,
          backgroundColor: light.scrollbarFg,
          border: `3px solid ${light.scrollbarBg}`,
          ...theme.applyStyles('dark', {
            backgroundColor: dark.scrollbarFg,
            border: `3px solid ${dark.scrollbarBg}`,
          }),
        },
      }),
    },
    MuiAppBar: {
      /**
       * Material UI throws away an AppBar's own colour in dark mode.
       *
       * `enableColorOnDark` defaults to `false`, and when it is false MUI
       * replaces the background with `background.paper` for any colour other
       * than `inherit` or `transparent` — so an app bar told to be navy came
       * out `#0b1f33` in dark and nothing warned about it. The app's chrome
       * does not follow the scheme, so the override has to be off.
       */
      defaultProps: { enableColorOnDark: true },
    },
    MuiAccordion: {
      styleOverrides: {
        /**
         * MUI's own heading slot is a styled `h3` carrying `all: unset`, so it
         * contributes nothing. Passing `slots={{ heading: 'h2' }}` — or the
         * redundant `'h3'` — swaps that styled wrapper for a bare tag, the
         * class stays but its styles do not, and the browser's heading
         * defaults come back: `font-size: 1.17em` and `margin-block: 1em`.
         * CssBaseline resets the body's margin, not headings'.
         *
         * Measured at 18.72px above and below every summary, which is what put
         * a visible gap between a grouped item's `Divider` and the next panel.
         * The rule is on the root rather than the `heading` slot because a
         * bare tag never renders the styled component a slot override targets.
         * The heading is a wrapper, never type — it inherits both.
         */
        root: {
          [`& > .${accordionClasses.heading}`]: {
            margin: 0,
            font: 'inherit',
            color: 'inherit',
          },
          /**
           * MUI draws its own 1px seam with `::before`, and then takes it away
           * again: `opacity: 0` while a panel is expanded, and `display: none`
           * on the panel following an expanded one. A separator that vanishes
           * exactly when someone opens something is not one you can build on,
           * so the seam is an explicit `Divider` in every recipe here and this
           * is switched off rather than left to fight with it.
           *
           * Measured before removing it: the two landed 0.33px apart — one
           * line to look at, two rules of `divider` compositing into something
           * fractionally darker than every other rule on the page.
           */
          '&::before': { display: 'none' },
        },
      },
    },
    MuiAccordionSummary: {
      /**
       * A 44px row, not Material UI's 48.
       *
       * This system already rejected MUI's one-size-fits-all 48px row: the
       * List page ships 44px large and 38px medium, and the Sidebar rail is
       * 36px. An accordion summary is a row in a list of sections, so it takes
       * the large row's height and stops being the tallest thing on the page.
       *
       * Both halves are needed. `minHeight` alone does nothing, because MUI's
       * 12px content margins plus a 28px `subtitle1` line already measure 52px
       * and a min can only push a box taller. 8px margins bring the content to
       * 44px and the min then holds it there when the label is shorter.
       */
      styleOverrides: {
        root: { minHeight: 44, '&.Mui-expanded': { minHeight: 44 } },
        content: { marginTop: 8, marginBottom: 8, '&.Mui-expanded': { marginTop: 8, marginBottom: 8 } },
      },
    },
    MuiTypography: {
      /**
       * Material UI maps `subtitle1` and `subtitle2` onto `<h6>`. That turns
       * every field label — "Badge number", "Email address" — into a heading:
       * the User Detail screen alone was emitting 22 of them, so a screen
       * reader navigating by heading walked the whole form instead of the
       * page's four real sections. Subtitles are labels, so they are
       * paragraphs; anything that is genuinely a heading says so with
       * `component`.
       */
      defaultProps: {
        variantMapping: { subtitle1: 'p', subtitle2: 'p' },
      },
    },
    MuiButtonGroup: {
      /**
       * `disableElevation` on `MuiButton` does not reach a ButtonGroup.
       * `MuiButtonGroup-contained` paints elevation-2 on the *group root*, and
       * the buttons inside it are already flat — so the shadow survives every
       * per-button default and shows up only on the contained variant. It has
       * to be turned off on the group as well, or a split button is the one
       * raised control in a flat system.
       */
      defaultProps: { disableElevation: true },
    },
    MuiButton: {
      // The Figma library draws buttons flat. Fab deliberately keeps its shadow —
      // it binds paper/elevation-6 in Figma, where Button binds no elevation.
      defaultProps: { disableElevation: true },
      styleOverrides: {
        sizeSmall: { fontSize: '0.8125rem', lineHeight: 22 / 13, letterSpacing: '0.46px' },
        sizeMedium: { fontSize: '0.875rem', lineHeight: 24 / 14, letterSpacing: '0.4px' },
        sizeLarge: { fontSize: '0.9375rem', lineHeight: 26 / 15, letterSpacing: '0.46px' },
      },
      /**
       * xlarge and xxlarge. Padding is per variant because MUI's own ramp is:
       * outlined loses 1px to its border so it matches contained, and text
       * halves the horizontal padding. Following that keeps the two new sizes
       * consistent with the three that already exist.
       */
      variants: [
        {
          props: { size: 'xlarge' },
          style: {
            ...buttonSizeTypography.xlarge,
            [`& .${buttonClasses.startIcon}`]: { marginRight: 10, marginLeft: -6 },
            [`& .${buttonClasses.endIcon}`]: { marginLeft: 10, marginRight: -6 },
            [`& .${buttonClasses.startIcon} > *:nth-of-type(1)`]: { fontSize: 28 },
            [`& .${buttonClasses.endIcon} > *:nth-of-type(1)`]: { fontSize: 28 },
            // Button hard-codes <CircularProgress size={16} />, which lands as an
            // inline width/height. !important is the only way to reach it, and a
            // 16px spinner beside 25px text reads as broken.
            [`& .${circularProgressClasses.root}`]: {
              width: '20px !important',
              height: '20px !important',
            },
          },
        },
        { props: { size: 'xlarge', variant: 'contained' }, style: { padding: '14px 26px' } },
        { props: { size: 'xlarge', variant: 'outlined' }, style: { padding: '13px 25px' } },
        { props: { size: 'xlarge', variant: 'text' }, style: { padding: '14px 13px' } },
        {
          props: { size: 'xxlarge' },
          style: {
            ...buttonSizeTypography.xxlarge,
            [`& .${buttonClasses.startIcon}`]: { marginRight: 12, marginLeft: -8 },
            [`& .${buttonClasses.endIcon}`]: { marginLeft: 12, marginRight: -8 },
            [`& .${buttonClasses.startIcon} > *:nth-of-type(1)`]: { fontSize: 36 },
            [`& .${buttonClasses.endIcon} > *:nth-of-type(1)`]: { fontSize: 36 },
            // See the xlarge note above — the hard-coded spinner needs !important.
            [`& .${circularProgressClasses.root}`]: {
              width: '26px !important',
              height: '26px !important',
            },
          },
        },
        { props: { size: 'xxlarge', variant: 'contained' }, style: { padding: '16px 32px' } },
        { props: { size: 'xxlarge', variant: 'outlined' }, style: { padding: '15px 31px' } },
        { props: { size: 'xxlarge', variant: 'text' }, style: { padding: '16px 16px' } },
      ],
    },
    MuiIconButton: {
      /**
       * Square, so padding plus icon is the whole story: 16 + 28 = 60 at
       * xlarge, 18 + 36 = 72 at xxlarge. The icon sizes match the ones Button
       * uses at the same step.
       *
       * MUI sets `fontSize` on the button for its own sizes, but a default
       * `SvgIcon` carries its own 24px and ignores it — which is why a large
       * IconButton is 48 rather than the 52 its padding and fontSize imply.
       * Handing the icon `fontSize: inherit` is what makes these two sizes
       * actually scale.
       */
      variants: [
        {
          props: { size: 'xlarge' },
          style: {
            padding: 16,
            fontSize: '1.75rem',
            [`& .${svgIconClasses.root}`]: { fontSize: 'inherit' },
          },
        },
        {
          props: { size: 'xxlarge' },
          style: {
            padding: 18,
            fontSize: '2.25rem',
            [`& .${svgIconClasses.root}`]: { fontSize: 'inherit' },
          },
        },
      ],
    },
    MuiAvatar: {
      styleOverrides: {
        // Figma avatar/initialsLg. MUI hardcodes 20px with no tracking.
        root: componentTypography.avatarInitials,
        colorDefault: ({ theme }) => ({
          backgroundColor: light.avatarFill,
          color: light.avatarText,
          ...theme.applyStyles('dark', {
            backgroundColor: dark.avatarFill,
            color: dark.avatarText,
          }),
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          // Figma tooltip/label. MUI ships 11px Medium, which was off the scale
          // in both size and weight — the only text in the system that was.
          ...componentTypography.tooltipLabel,
          backgroundColor: light.tooltipFill,
          ...theme.applyStyles('dark', { backgroundColor: dark.tooltipFill }),
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        // Figma chip/label. MUI sets the size on the root and the label
        // inherits it, so the style goes on the root.
        root: componentTypography.chipLabel,
      },
      variants: STATUS_CHIP_COLORS.map((color) => ({
        /**
         * A wash of the palette colour rather than a solid fill: 14% ground,
         * 52% border, `text.primary` ink.
         *
         * One variant per colour rather than one reading `ownerState`, because
         * MUI does not type `ownerState` in a `variants` style callback — the
         * loop keeps it to a single rule either way.
         *
         * Figma tints success and error from `.dark` and warning from `.main`.
         * This uses `.main` for all three: one rule beats three special cases,
         * and at 14% the two stops are indistinguishable.
         */
        props: { variant: 'status', color },
        style: ({ theme }) => {
          /**
           * `theme.vars`, not `theme.palette` — and this is the whole reason the
           * first version of this variant was broken.
           *
           * The theme runs on CSS variables, so `theme.palette.success.main`
           * hands back the *light* scheme's literal hex, baked in at build time.
           * The chips rendered a light-mode green wash with navy ink on the dark
           * surface, in a story whose scheme had genuinely switched. Only
           * `theme.vars.*` emits the `var(--mui-palette-…)` that follows the
           * scheme.
           *
           * `alpha()` cannot tint a CSS variable, so the channel tokens do it:
           * `mainChannel` is an "R G B" triplet made for exactly this.
           */
          const channel = theme.vars?.palette[color].mainChannel;
          const ink = theme.vars?.palette.text.primary ?? theme.palette.text.primary;
          const wash = (opacity: number) =>
            channel ? `rgba(${channel} / ${opacity})` : alpha(theme.palette[color].main, opacity);

          return {
            color: ink,
            backgroundColor: wash(0.14),
            border: `1px solid ${wash(0.52)}`,
            [`& .${chipClasses.deleteIcon}`]: { color: wash(0.7) },
          };
        },
      })),
    },
    MuiOutlinedInput: {
      styleOverrides: {
        /**
         * A read-only field is not interactive, so it must not behave as
         * though it is: no hover shift, and no focused outline just because a
         * pointer landed on it. It reads as the default state.
         *
         * Keyboard focus is the exception, and it is not negotiable. The field
         * is still reachable by Tab — someone has to be able to see where they
         * are — so `:focus-visible` restores a real ring. `.Mui-focused` fires
         * for a mouse click too, which is the interaction being removed;
         * `:focus-visible` fires only when the browser judges a focus ring is
         * warranted, which is exactly the distinction wanted here.
         *
         * The resting colour is restated rather than tokenised because MUI
         * computes it inline per mode and publishes no variable for it — there
         * is no `--mui-palette-OutlinedInput-border` to point at.
         */
        root: ({ theme }) => {
          const resting = {
            borderColor: 'rgba(0, 0, 0, 0.23)',
            ...theme.applyStyles('dark', { borderColor: 'rgba(255, 255, 255, 0.23)' }),
          };
          return {
            [`&.${inputBaseClasses.readOnly}`]: {
              [`& .${outlinedInputClasses.input}`]: { cursor: 'default' },
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: resting,
              [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]: {
                ...resting,
                borderWidth: 1,
              },
              [`&:has(:focus-visible) .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: theme.vars?.palette.primary.main ?? theme.palette.primary.main,
                borderWidth: 2,
              },
            },
          };
        },
      },
    },
    MuiAlert: {
      variants: STATUS_ALERT_COLORS.map((color) => ({
        /**
         * The status chip's wash, on a banner: 14% ground, 52% border,
         * `text.primary` ink, and the icon alone at full strength so the tone
         * still reads at a glance without the text changing colour.
         *
         * One entry per colour for the same reason the Chip has one — MUI does
         * not type `ownerState` in a `variants` style callback.
         *
         * `theme.vars`, never `theme.palette`: the callback runs once, at
         * theme creation, so `theme.palette` would bake the light scheme's
         * literal into the class and the banner would keep its light wash on
         * the navy surface. Only `theme.vars.*` emits the variable that flips.
         */
        props: { variant: 'status', color },
        style: ({ theme }) => {
          // `default` is the neutral tone, painted from the ink rather than
          // from a palette colour, because no severity is grey.
          const channel =
            color === 'default'
              ? theme.vars?.palette.text.secondaryChannel
              : theme.vars?.palette[color].mainChannel;
          const ink = theme.vars?.palette.text.primary ?? theme.palette.text.primary;
          // `alpha()` cannot tint a CSS variable, so the channel token does it.
          const wash = (opacity: number) =>
            channel
              ? `rgba(${channel} / ${opacity})`
              : alpha(color === 'default' ? theme.palette.text.secondary : theme.palette[color].main, opacity);

          return {
            color: ink,
            backgroundColor: wash(0.14),
            border: `1px solid ${wash(0.52)}`,
            [`& .${alertClasses.icon}`]: { color: channel ? `rgb(${channel})` : undefined },
            // The close button inherits the banner's ink rather than MUI's
            // severity colour, which the wash has already replaced.
            [`& .${alertClasses.action}`]: { color: ink },
          };
        },
      })),
    },
    MuiBadge: {
      styleOverrides: {
        // Figma badge/label. MUI defaults the weight to Medium.
        badge: componentTypography.badgeLabel,
      },
      variants: [
        {
          // In flow beside a heading, not pinned to a corner. Only the two
          // properties that mean "pinned" change; the pill is MUI's own.
          props: { variant: 'standalone' },
          style: {
            [`& .${badgeClasses.badge}`]: {
              position: 'static',
              transform: 'none',
            },
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        /**
         * Row rules are the divider token, like every other line in the system.
         *
         * MUI gives `TableCell` its own `palette.TableCell.border` — an opaque
         * neutral grey — rather than `palette.divider`. In light the two land
         * on the same pixel: `rgba(0,0,0,0.12)` over white composites to
         * exactly `#e0e0e0`. In dark they part company badly, because the
         * divider is a translucent white that picks up the navy surface under
         * it while MUI's grey stays neutral — a table drew warm grey rules
         * inside a card whose own edges were blue-grey.
         *
         * One CSS variable covers both schemes, so there is no `applyStyles`
         * pair to keep in step.
         */
        root: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
        // The Figma table/header style is Semi Bold; MUI defaults to Medium.
        head: { fontWeight: fontWeights.semiBold },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: ({ theme }) => ({
          backgroundColor: light.switchTrack,
          ...theme.applyStyles('dark', { backgroundColor: dark.switchTrack }),
        }),
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: { color: ratingActiveFill },
      },
    },
    MuiListSubheader: {
      defaultProps: { size: 'medium' },
      styleOverrides: {
        // MUI hard-codes lineHeight: 48px to set the height. Figma sizes both
        // subheaders from their own line height plus 8px above and below —
        // 44px large, 38px medium — so the line height has to come back out.
        root: ({ theme }) => ({
          color: theme.vars.palette.text.secondary,
          paddingTop: 8,
          paddingBottom: 8,
          ...theme.typography.subtitle2,
        }),
      },
      variants: [
        {
          props: { size: 'large' },
          style: ({ theme }) => ({ ...theme.typography.subtitle1 }),
        },
      ],
    },
  },
});

export * from './palette';
export * from './typography';
export default surepassTheme;
